import type { PaymentMethod } from '@massilia/prisma-generated'
import type { BookingSessionResponse } from '@massilia/queries'

import { useAppForm } from '@massilia/features'
import {
  setAccessToken,
  useApplyPromoCode,
  useCheckoutRegisterMutation,
  useInitiateSessionCheckout,
  useRemovePromoCode,
  useSubmitDepositReservation,
} from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { differenceInMonths } from 'date-fns'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { TripHeader } from '@/components/booking/trip-header'
import { useAuthStore } from '@/stores/auth.store'

import { CheckoutPromoCodeSlot } from './-sections/checkout-promo-code-slot'
import { ContactInfoSection } from './-sections/contact-info-section'
import { PaymentMethodSelector } from './-sections/payment-method-selector'
import { PaymentSection } from './-sections/payment-section'
import { PaymentTimingSection } from './-sections/payment-timing-section'
import { StepCard } from './-sections/step-card'
import {
  checkoutFormSchema,
  type CheckoutFormType,
  paymentMethods,
  type PaymentTimingFormValue,
} from './checkout-form.schema'

interface CheckoutComponentProps {
  prefillEmail?: string
  sessionId: string
  isLoading: boolean
  prefillPhone?: string
  session?: BookingSessionResponse
}

export function CheckoutComponent({
  prefillEmail,
  sessionId,
  isLoading,
  prefillPhone,
  session,
}: CheckoutComponentProps) {
  const navigate = useNavigate()
  const updateAuth = useAuthStore((state) => state.updateAuth)

  // Promo code state: the discount itself lives on the session (server is source of truth).
  // Only the transient error message is kept in local state.
  const [promoCodeError, setPromoCodeError] = useState<null | string>(null)

  const { isPending: isPromoCodePending, mutateAsync: applyPromoCode } =
    useApplyPromoCode(sessionId)
  const { mutateAsync: removePromoCode } = useRemovePromoCode(sessionId)
  const { isPending: isCheckoutPending, mutateAsync: initiateCheckout } =
    useInitiateSessionCheckout(sessionId)
  const { isPending: isDepositPending, mutateAsync: submitDepositReservation } =
    useSubmitDepositReservation(sessionId)
  const { mutateAsync: checkoutRegister } = useCheckoutRegisterMutation()

  const promoCodeDiscount = session?.promoCode ? session.promoCode.discountCents / 100 : null

  // Données dérivées — all from session (server is source of truth)
  const flightPriceCents = session?.totalFlightPriceCents ?? 0
  const insurancePriceCents = session?.insurance?.insurancePriceCents ?? 0
  const flightPrice = flightPriceCents / 100
  const insurancePrice = insurancePriceCents / 100
  const totalPrice = flightPrice + insurancePrice
  const paymentDeadline = session?.installmentPaymentDeadline
  const depositDeadline = session?.depositDeadline
  const isReunion = session?.departureAirportCode === 'RUN'

  const installmentAvailable = session?.installmentInfo?.available ?? false
  const minimumDeposit = installmentAvailable
    ? (session?.installmentInfo?.minimumDepositCents ?? 0) / 100
    : 0

  const isFloaDisabled =
    installmentAvailable &&
    !!session?.flightDetails?.departureDate &&
    differenceInMonths(session.flightDetails.departureDate, new Date()) > 4

  const form = useAppForm({
    onSubmitInvalid: () => {
      const invalidInput = document.querySelector('[aria-invalid="true"]') as HTMLInputElement
      invalidInput?.scrollIntoView({ behavior: 'smooth', inline: 'center' })
      invalidInput?.focus()
    },
    defaultValues: {
      buyerEmail: prefillEmail ?? '',
      billingCity: '',
      billingCountryCode: { label: '', value: '' },
      billingStreet: '',
      billingZipCode: '',
      buyerPhone: prefillPhone ?? '',
      createAccount: false,
      depositAmount: minimumDeposit,
      floaPayType: '3XG' as '3XG' | '4XG',
      isPayingInMultipleTimes: false,
      password: '',
      passwordConfirmation: '',
      paymentMethod: 'credit-card' as (typeof paymentMethods)[number],
      paymentTiming: 'PAY_NOW' as PaymentTimingFormValue,
      promoCode: '',
      subscribeToNotifications: false,
    } satisfies CheckoutFormType,
    onSubmit: async ({ value }) => {
      const tryCreateAccount = async (email: string) => {
        if (!value.createAccount || !value.password || !value.passwordConfirmation) return

        try {
          const user = await checkoutRegister({
            email: email.trim(),
            password: value.password,
            passwordConfirmation: value.passwordConfirmation,
          })

          setAccessToken(user.accessToken)
          updateAuth({
            isAuthenticated: true,
            user: {
              email: user.email,
              firstName: user.firstName,
              id: user.id,
              lastName: user.lastName,
            },
          })
        } catch {
          // Account creation failed (e.g., account already exists).
          // Do not block the checkout — the booking is already created.
        }
      }

      // "Pay later" flow: skip Axepta, record the reservation request, redirect to pending.
      if (value.paymentTiming === 'PAY_LATER') {
        const result = await submitDepositReservation({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        const bookingReference = result.checkout?.bookingReference ?? ''
        void navigate({
          params: { status: 'pending' },
          search: { bookingRef: bookingReference, session: sessionId },
          to: '/payment-confirmation/$status',
        })
        return
      }

      const shouldPayDeposit = installmentAvailable && value.isPayingInMultipleTimes

      if (value.paymentMethod === 'credit-card' || value.paymentMethod === 'floa') {
        // Redirect payment flow (credit card or FLOA)
        const isFloa = value.paymentMethod === 'floa'

        const result = await initiateCheckout({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
          paymentMethod: (isFloa ? 'INSTALLMENTS' : 'CREDIT_CARD') as PaymentMethod,
          paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
          subscribeToNotifications: value.subscribeToNotifications,
          ...(isFloa && {
            billingAddress: {
              city: value.billingCity,
              countryCode: value.billingCountryCode.value,
              street: value.billingStreet,
              zipCode: value.billingZipCode,
            },
            floaPayType: value.floaPayType,
          }),
        })

        await tryCreateAccount(value.buyerEmail)

        const checkout = result.checkout
        if (!checkout?.axeptaRedirectUrl) {
          throw new Error('No checkout redirect URL returned from server')
        }

        window.location.assign(checkout.axeptaRedirectUrl)
      } else {
        const paymentMethodMap: Record<string, PaymentMethod> = {
          'bank-transfer': 'BANK_TRANSFER',
          'cash': 'CASH',
          'vacation-cheque': 'VACATION_CHEQUE',
          'vacation-e-cheque': 'VACATION_E_CHEQUE',
        }

        const result = await initiateCheckout({
          buyerEmail: value.buyerEmail.trim(),
          buyerPhone: value.buyerPhone.trim(),
          depositAmount: shouldPayDeposit ? value.depositAmount : undefined,
          paymentMethod: paymentMethodMap[value.paymentMethod]!,
          paymentType: shouldPayDeposit ? 'DEPOSIT' : 'FULL',
          subscribeToNotifications: value.subscribeToNotifications,
        })

        await tryCreateAccount(value.buyerEmail)

        // Non-CC flow: navigate client-side to the pending confirmation page
        const bookingReference = result.checkout?.bookingReference ?? ''
        void navigate({
          params: { status: 'pending' },
          search: { bookingRef: bookingReference, session: sessionId },
          to: '/payment-confirmation/$status',
        })
      }
    },
    validationLogic: revalidateLogic({ mode: 'submit', modeAfterSubmission: 'blur' }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, checkoutFormSchema),
    },
  })

  const handleVerifyPromoCode = async (code: string) => {
    if (!code.trim()) return
    setPromoCodeError(null)
    try {
      await applyPromoCode({ code: code.trim() })
    } catch (e) {
      const fallback = 'Code promo invalide.'
      const message =
        e instanceof AxiosError && typeof e.response?.data?.message === 'string'
          ? e.response.data.message
          : fallback
      setPromoCodeError(message)
    }
  }

  const handleRemovePromoCode = async () => {
    setPromoCodeError(null)
    await removePromoCode()
    form.setFieldValue('promoCode', '')
  }

  const handleBack = () => {
    void navigate({
      search: { session: sessionId },
      to: '/assurances',
    })
  }

  if (isLoading && !session) {
    return (
      <div className="flex w-full items-center justify-center py-20">
        <Loader2 className="text-brand-600 size-8 animate-spin" />
      </div>
    )
  }

  return (
    <>
      <form
        className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        {/* Mobile-only TripHeader */}
        {session && (
          <div className="lg:hidden">
            <TripHeader session={session} />
          </div>
        )}

        {/* Colonne droite — Sidebar */}
        <div className="w-full shrink-0 lg:order-last lg:w-96">
          <BookingSidebar
            promoCodeDiscount={promoCodeDiscount}
            promoCodeSlot={
              <CheckoutPromoCodeSlot
                appliedCode={session?.promoCode?.code ?? null}
                fields={{ promoCode: 'promoCode' }}
                form={form}
                isVerifying={isPromoCodePending}
                onRemove={handleRemovePromoCode}
                onVerify={handleVerifyPromoCode}
                promoCodeDiscount={promoCodeDiscount}
                promoCodeError={promoCodeError}
              />
            }
            session={session}
          />
        </div>

        {/* Colonne gauche — Formulaire */}
        <div className="flex flex-1 flex-col gap-9">
          {/* Desktop-only TripHeader */}
          {session && (
            <div className="hidden lg:block">
              <TripHeader session={session} />
            </div>
          )}

          <div className="flex w-full flex-col gap-2">
            <div className="text-gray-light-900 text-lg font-semibold">
              Résumé des informations et paiement
            </div>
            <div className="text-gray-light-900 w-full text-sm">
              Vous avez sélectionné les services suivants pour votre voyage. Veuillez vérifier les
              informations avant de finaliser votre réservation.
            </div>
          </div>

          <div className="flex w-full flex-col gap-6">
            {/* Informations de contact + options compte */}
            <ContactInfoSection
              fields={{
                buyerEmail: 'buyerEmail',
                buyerPhone: 'buyerPhone',
                createAccount: 'createAccount',
                password: 'password',
                passwordConfirmation: 'passwordConfirmation',
                paymentMethod: 'paymentMethod',
                subscribeToNotifications: 'subscribeToNotifications',
              }}
              form={form}
            />

            <form.Subscribe selector={(state) => state.values.paymentTiming}>
              {(paymentTiming) => (
                <>
                  {/* Délai de paiement — maintenant ou avant la date d'acompte */}
                  <StepCard
                    step={1}
                    subtitle="Choisissez quand vous souhaitez régler votre voyage"
                    title="Délai de paiement"
                  >
                    <PaymentTimingSection
                      depositDeadline={depositDeadline}
                      fields={{ paymentTiming: 'paymentTiming' }}
                      form={form}
                    />
                  </StepCard>

                  {paymentTiming === 'PAY_NOW' && (
                    <>
                      {/* Type de paiement — Intégralité / Plusieurs fois */}
                      {installmentAvailable && (
                        <StepCard
                          step={2}
                          subtitle="Réglez en intégralité ou en plusieurs fois"
                          title="Type de paiement"
                        >
                          <PaymentSection
                            fields={{
                              depositAmount: 'depositAmount',
                              isPayingInMultipleTimes: 'isPayingInMultipleTimes',
                            }}
                            form={form}
                            installmentAvailable={installmentAvailable}
                            minimumDeposit={minimumDeposit}
                            promoCodeDiscount={promoCodeDiscount}
                            totalPrice={totalPrice}
                          />
                        </StepCard>
                      )}

                      {/* Mode de paiement — CB, virement, etc. */}
                      <StepCard
                        step={installmentAvailable ? 3 : 2}
                        subtitle="Tous les paiements sont sécurisés avec nos prestataires"
                        title="Mode de paiement"
                      >
                        <PaymentMethodSelector
                          fields={{
                            billingCity: 'billingCity',
                            billingCountryCode: 'billingCountryCode',
                            billingStreet: 'billingStreet',
                            billingZipCode: 'billingZipCode',
                            floaPayType: 'floaPayType',
                            paymentMethod: 'paymentMethod',
                          }}
                          form={form}
                          paymentDeadline={paymentDeadline}
                          isFloaDisabled={isFloaDisabled}
                          isReunion={isReunion}
                          totalPrice={totalPrice}
                        />
                      </StepCard>
                    </>
                  )}
                </>
              )}
            </form.Subscribe>

            {/* Boutons de navigation */}
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                className="hover:bg-gray-light-50 gap-2"
                onClick={handleBack}
                type="button"
                variant="none"
              >
                <ChevronLeft className="text-brand-700 h-4 w-4" />
                <div className="text-brand-700 text-sm font-medium">Étape précédente</div>
              </Button>
              <form.Subscribe
                selector={(state) => ({
                  isSubmitting: state.isSubmitting,
                  paymentMethod: state.values.paymentMethod,
                  paymentTiming: state.values.paymentTiming,
                })}
              >
                {({ isSubmitting, paymentMethod, paymentTiming }) => {
                  const submitLabel =
                    paymentTiming === 'PAY_LATER'
                      ? 'Confirmer ma demande'
                      : paymentMethod === 'credit-card' || paymentMethod === 'floa'
                        ? 'Payer'
                        : 'Réserver'
                  return (
                    <Button
                      className={
                        paymentTiming === 'PAY_LATER' ? 'gap-2 px-4' : 'w-32 gap-2'
                      }
                      disabled={isSubmitting || isCheckoutPending || isDepositPending}
                      type="submit"
                    >
                      {isSubmitting || isCheckoutPending || isDepositPending ? (
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                      ) : (
                        <div className="text-sm font-medium text-white">{submitLabel}</div>
                      )}
                    </Button>
                  )
                }}
              </form.Subscribe>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}
